import { Fragment, useEffect } from 'react';
import { get, set } from 'lodash';
import * as uuid from 'uuid';
import { IconButton } from '@lendi-ui/button';
import getColour from '@lendi-ui/commons/colours';
import Add from '@lendi-ui/icon/Add';
import Cancel from '@lendi-ui/icon/Cancel';
import { Body } from '@lendi-ui/typography';

import { FIELD_PATHS, FREQUENCY, INCOME_TYPE } from '@funnels/constants';
import { useFormValues } from '@funnels/hooks';
import { useAnalytics } from '@funnels/providers';
import { EmploymentEntityIndex, Income } from '@funnels/types';
import { AddButton, Divider, FunnelGrid, FunnelGridUnit } from '@funnels/ui-components/Common';
import { EmploymentIncomeAmount, EmploymentIncomeFrequency, EmploymentIncomeType } from '@funnels/ui-components/Income';
import { initialFormValues } from '@funnels/utils';

interface EmploymentIncomeProps extends EmploymentEntityIndex {}

export const EmploymentIncome = (props: EmploymentIncomeProps) => {
  const { tracking } = useAnalytics();
  const { employmentIndex } = props;
  const [formValues, setFormValue] = useFormValues();
  const employmentClientId = get(formValues, FIELD_PATHS.EMPLOYMENT_CLIENT_ID(employmentIndex));
  const incomes: Income[] = get(formValues, FIELD_PATHS.INCOMES(), []);
  const baseSalaryIncomeIndex = incomes.findIndex(
    (income) =>
      get({ incomes: [income] }, FIELD_PATHS.INCOME_EMPLOYMENT_CLIENT_ID(0)) === employmentClientId &&
      get({ incomes: [income] }, FIELD_PATHS.INCOME_TYPE(0)) === INCOME_TYPE.PAYG_BASE_SALARY
  );
  const hasAdditionalIncome = !!incomes.find(
    (income, index) =>
      get({ incomes: [income] }, FIELD_PATHS.INCOME_EMPLOYMENT_CLIENT_ID(0)) === employmentClientId &&
      index !== baseSalaryIncomeIndex
  );
  let additionalIncomeCount = 0;

  useEffect(() => {
    if (baseSalaryIncomeIndex === -1) {
      // just in case there is no PAYG base salary
      const newBaseSalaryIncomeIncome = initialFormValues.INITIAL_PAYG_BASE_SALARY_INCOME();
      set({ incomes: [newBaseSalaryIncomeIncome] }, FIELD_PATHS.INCOME_EMPLOYMENT_CLIENT_ID(0), employmentClientId);
      set({ incomes: [newBaseSalaryIncomeIncome] }, FIELD_PATHS.INCOME_FREQUENCY(0), FREQUENCY.YEARLY);
      setFormValue(FIELD_PATHS.INCOME(incomes.length), newBaseSalaryIncomeIncome);
    }
  }, [baseSalaryIncomeIndex, employmentClientId, incomes.length, setFormValue]);

  return (
    <>
      <FunnelGridUnit mb="md">
        <EmploymentIncomeAmount label="Annual base salary before tax" incomeIndex={baseSalaryIncomeIndex} />
      </FunnelGridUnit>
      {hasAdditionalIncome && (
        <FunnelGridUnit mb="sm">
          <Body>Additional earnings for this job (if any)</Body>
        </FunnelGridUnit>
      )}
      {incomes.map((income, index) => {
        if (
          get({ incomes: [income] }, FIELD_PATHS.INCOME_EMPLOYMENT_CLIENT_ID(0)) === employmentClientId &&
          index !== baseSalaryIncomeIndex
        ) {
          const incomeClientId = get({ incomes: [income] }, FIELD_PATHS.INCOME_CLIENT_ID(0));
          const incomeType = get({ incomes: [income] }, FIELD_PATHS.INCOME_TYPE(0));
          const isFromCompanyCar = incomeType === INCOME_TYPE.PAYG_COMPANY_CAR;
          return (
            <Fragment key={`income-${incomeClientId}`}>
              {additionalIncomeCount++ > 0 && (
                <FunnelGridUnit mb="md">
                  <Divider />
                </FunnelGridUnit>
              )}
              <FunnelGrid mb="md" wrap={{ xs: false }}>
                <FunnelGridUnit>
                  <EmploymentIncomeType incomeIndex={index} shouldHideSalaryOption />
                </FunnelGridUnit>
                <FunnelGridUnit size="min">
                  <IconButton
                    aria-label="Remove income"
                    icon={Cancel}
                    size="md"
                    type="button"
                    onClick={() =>
                      setFormValue(
                        FIELD_PATHS.INCOMES(),
                        incomes.filter((_, i) => i !== index)
                      )
                    }
                  />
                </FunnelGridUnit>
              </FunnelGrid>
              {!isFromCompanyCar && (
                <FunnelGridUnit mb="md" size={{ xs: 1 / 2, sm: 1 / 2, md: 3 / 5, lg: 3 / 5, xl: 3 / 5 }}>
                  <EmploymentIncomeAmount incomeIndex={index} aria-label="How much is the additional earning?" />
                </FunnelGridUnit>
              )}
              {!isFromCompanyCar && (
                <FunnelGridUnit mb="md" size={{ xs: 1 / 2, sm: 1 / 2, md: 2 / 5, lg: 2 / 5, xl: 2 / 5 }}>
                  <EmploymentIncomeFrequency
                    incomeIndex={index}
                    aria-label="How frequently is the additional earning received?"
                  />
                </FunnelGridUnit>
              )}
            </Fragment>
          );
        } else {
          return null;
        }
      })}
      <AddButton
        variant="empty"
        before={<Add color={getColour('interaction', 'main')} width="1.5em" height="1.5em" />}
        data-component="add-button"
        onClick={() => {
          const newIncome = { clientId: uuid.v4() };
          set({ incomes: [newIncome] }, FIELD_PATHS.INCOME_EMPLOYMENT_CLIENT_ID(0), employmentClientId);
          setFormValue(FIELD_PATHS.INCOME(incomes.length), newIncome);
          tracking?.button.empty({ text: 'Add additional earning' });
        }}
        isFullWidth
      >
        Add additional earning
      </AddButton>
    </>
  );
};
